import { protectedStorefrontFetch } from './storefront-fetch.js';

const RATING_ENDPOINT = '/api/article-ratings';
const VOTE_STORAGE_PREFIX = 'ippodo:article-rating:';

export function calculateArticleRating(value, count, rating) {
  const currentValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  const currentCount = Number.isFinite(Number(count)) ? Math.max(0, Number(count)) : 0;
  const nextCount = currentCount + 1;
  const nextValue =
    Math.round(((currentValue * currentCount + Number(rating)) / nextCount) * 100) / 100;
  return { value: nextValue, count: nextCount };
}

function getStoredVote(articleId) {
  try {
    return window.localStorage.getItem(`${VOTE_STORAGE_PREFIX}${articleId}`);
  } catch {
    return null;
  }
}

function storeVote(articleId, rating) {
  try {
    window.localStorage.setItem(`${VOTE_STORAGE_PREFIX}${articleId}`, String(rating));
  } catch {
    // The server result remains authoritative when storage is unavailable.
  }
}

function setAggregate(elements, aggregate) {
  elements.value.textContent = String(aggregate.value);
  elements.count.textContent = String(aggregate.count);
  elements.plural.textContent = aggregate.count === 1 ? '' : 's';
  elements.summary.removeAttribute('hidden');
  elements.invite?.setAttribute('hidden', '');
}

function setStars(stars, rating) {
  stars.forEach((star) => {
    const selected = Number(star.dataset.rating) <= rating;
    star.classList.toggle('star-on', selected);
    star.classList.toggle('star-off', !selected);
    star.setAttribute('aria-pressed', String(Number(star.dataset.rating) === rating));
  });
}

function setSubmittedState(elements, message) {
  elements.triggerStars.forEach((star) => {
    star.disabled = true;
  });
  elements.triggerSelected.textContent = message;
  elements.triggerLabel.setAttribute('hidden', '');
}

function formatCommentDate(value, documentRoot) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(documentRoot.documentElement.lang || 'en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function appendComment(elements, comment, { pending = false } = {}) {
  if (!comment?.body) return null;
  // Defense in depth with the Liquid filter: never render unapproved text,
  // even optimistically. Missing statuses are legacy pre-moderation comments.
  if (comment.status && comment.status !== 'approved') return null;
  const documentRoot = elements.commentsList.ownerDocument;
  const item = documentRoot.createElement('article');
  item.className = 'recipe-comment';
  item.dataset.ratingComment = pending ? 'pending' : comment.id;

  const header = documentRoot.createElement('div');
  header.className = 'recipe-comment__header';
  const byline = documentRoot.createElement('div');
  const author = documentRoot.createElement('strong');
  author.textContent = comment.author;
  const separator = documentRoot.createTextNode(' · ');
  const time = documentRoot.createElement('time');
  time.dateTime = comment.created_at;
  time.textContent = formatCommentDate(comment.created_at, documentRoot);
  byline.append(author, separator, time);

  const stars = documentRoot.createElement('span');
  stars.className = 'stars stars--comment';
  stars.style.setProperty('--fill', `${comment.rating * 20}%`);
  stars.setAttribute('role', 'img');
  stars.setAttribute('aria-label', `${comment.rating} out of 5 stars`);
  header.append(byline, stars);

  const body = documentRoot.createElement('p');
  String(comment.body)
    .split(/\r?\n/)
    .forEach((line, index) => {
      if (index > 0) body.append(documentRoot.createElement('br'));
      body.append(documentRoot.createTextNode(line));
    });
  item.append(header, body);
  elements.commentsList.prepend(item);
  elements.commentsSection.removeAttribute('hidden');
  return item;
}

function readElements(root) {
  return {
    value: root.querySelector('#currentValue'),
    count: root.querySelector('#currentCount'),
    plural: root.querySelector('#ratingPlural'),
    summary: root.querySelector('#rating-summary'),
    invite: root.querySelector('#rating-invite'),
    triggerWrapper: root.querySelector('.rating-trigger .stars-interactive'),
    triggerSelected: root.querySelector('.rating-trigger .rating-input__selected'),
    triggerLabel: root.querySelector('.rating-trigger .rating-input__label'),
    triggerStars: [...root.querySelectorAll('.rating-trigger .review-star')],
    status: root.querySelector('#rating-status'),
    dialog: root.querySelector('#article-rating-modal'),
    form: root.querySelector('#article-rating-form'),
    modalStars: [...root.querySelectorAll('.rating-modal-stars .review-star')],
    modalWrapper: root.querySelector('.rating-modal-stars'),
    modalSelected: root.querySelector('.rating-modal .rating-input__selected'),
    modalStatus: root.querySelector('#article-rating-modal-status'),
    comment: root.querySelector('#article-rating-comment'),
    author: root.querySelector('#article-rating-author'),
    commentsSection: root.querySelector('#recipe-comments'),
    commentsList: root.querySelector('#recipe-comments-list'),
  };
}

export function initializeArticleRating(root = document, submitRating = protectedStorefrontFetch) {
  const container = root.querySelector('#article-rating');
  if (!container) return;

  const elements = readElements(container);
  const articleId = container.dataset.articleid;
  if (!articleId || !elements.dialog || !elements.form) return;
  const articleTitle = container.dataset.articleTitle || '';
  const articleUrl = container.dataset.articleUrl || '';
  let ratingSaved = false;

  if (getStoredVote(articleId)) {
    setSubmittedState(elements, container.dataset.thanksLabel);
    return;
  }

  function selectModalRating(rating) {
    elements.form.dataset.rating = String(rating);
    setStars(elements.modalStars, rating);
    elements.modalWrapper.classList.add('has-selection');
    elements.modalSelected.textContent = container.dataset.selectedLabel.replace(
      '[rating]',
      String(rating)
    );
  }

  function openRatingModal(rating) {
    setStars(elements.triggerStars, rating);
    elements.triggerWrapper.classList.add('has-selection');
    selectModalRating(rating);
    elements.modalStatus.textContent = '';
    elements.dialog.showModal();
    elements.comment.focus();
  }

  elements.triggerStars.forEach((star) => {
    star.addEventListener('click', () => openRatingModal(Number(star.dataset.rating)));
  });
  elements.modalStars.forEach((star) => {
    star.addEventListener('click', () => selectModalRating(Number(star.dataset.rating)));
  });

  function closeRatingModal() {
    if (!elements.form.classList.contains('loading')) elements.dialog.close();
  }

  elements.dialog.addEventListener('close', () => {
    if (ratingSaved) return;
    setStars(elements.triggerStars, 0);
    elements.triggerWrapper.classList.remove('has-selection');
    elements.triggerSelected.textContent = '';
  });

  elements.dialog.querySelectorAll('[data-rating-modal-close]').forEach((button) => {
    button.addEventListener('click', closeRatingModal);
  });
  elements.dialog.addEventListener('click', (event) => {
    if (event.target === elements.dialog) closeRatingModal();
  });
  elements.dialog.addEventListener('cancel', (event) => {
    if (elements.form.classList.contains('loading')) event.preventDefault();
  });

  elements.form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const rating = Number(elements.form.dataset.rating);
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) return;

    const comment = elements.comment.value.trim();
    const author = elements.author.value.trim();
    if (comment.length < 3) {
      elements.comment.focus();
      elements.comment.reportValidity();
      return;
    }
    if (author.length < 2) {
      elements.modalStatus.textContent = container.dataset.authorRequiredLabel;
      elements.author.focus();
      elements.author.reportValidity();
      return;
    }

    const previous = {
      value: Number(elements.value.textContent) || 0,
      count: Number(elements.count.textContent) || 0,
      summaryHidden: elements.summary.hasAttribute('hidden'),
      inviteHidden: elements.invite?.hasAttribute('hidden') ?? true,
      commentsHidden: elements.commentsSection.hasAttribute('hidden'),
    };
    const optimistic = calculateArticleRating(previous.value, previous.count, rating);
    // No optimistic comment append: pending text stays hidden until moderated,
    // so even the author's own words never render before approval. The
    // aggregate still updates optimistically — a pending comment is a real
    // rating; only its text is held.

    setAggregate(elements, optimistic);
    container.classList.add('loading');
    container.setAttribute('aria-busy', 'true');
    elements.form.classList.add('loading');
    elements.form.querySelector('button[type="submit"]').disabled = true;
    elements.modalStatus.textContent = container.dataset.savingLabel;
    elements.status.textContent = '';

    try {
      const response = await submitRating(RATING_ENDPOINT, {
        articleId,
        rating,
        comment,
        author,
        articleTitle,
        articleUrl,
      });
      if (!response.ok) throw new Error(`Rating request returned HTTP ${response.status}`);

      const result = await response.json();
      setAggregate(elements, result);
      storeVote(articleId, rating);
      ratingSaved = true;
      setSubmittedState(elements, container.dataset.thanksLabel);
      if (result.comment && result.comment.status && result.comment.status !== 'approved') {
        // Held for Slack moderation: the text stays hidden until approved.
        elements.status.textContent =
          container.dataset.pendingLabel || container.dataset.savedLabel;
      } else {
        if (result.comment) appendComment(elements, result.comment);
        elements.status.textContent = container.dataset.savedLabel;
      }
      elements.form.reset();
      elements.dialog.close();
    } catch (error) {
      console.error('[article-rating]', error);
      if (previous.commentsHidden) elements.commentsSection.setAttribute('hidden', '');
      setAggregate(elements, previous);
      if (previous.summaryHidden) elements.summary.setAttribute('hidden', '');
      if (!previous.inviteHidden) elements.invite?.removeAttribute('hidden');
      elements.modalStatus.textContent = container.dataset.errorLabel;
      elements.status.textContent = container.dataset.errorLabel;
    } finally {
      container.classList.remove('loading');
      container.removeAttribute('aria-busy');
      elements.form.classList.remove('loading');
      elements.form.querySelector('button[type="submit"]').disabled = false;
    }
  });
}

if (typeof document !== 'undefined') {
  initializeArticleRating();
}
